import React, { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Accordion } from '../../../../../components/accordion';
import { EmptyStateBox } from '../../../../../components/empty-state-box';
import { useDebounce } from '../../../../../hooks/debounce/use-debounce';
import { ActionCreateDeviceVariants } from '../../../../../modules/action-create/device-variants';
import { ROUTES } from '../../../../../routes/routes';
import { ChannelBox } from '../../../components/channel/box';
import { useDevicesAndChannels } from '../../../hooks/devices-and-channels';
import { ChannelGroupItem, ChannelType, Visibility } from '../../../types';
import { useLavvaCreateActionContext } from '../context';
import { useActionTaskVariants } from '../hooks/use-action-task-variants';

const LavvaActionCreateDeviceVariants: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('action');
  const { channelGroups } = useDevicesAndChannels({ visibility: Visibility.All });
  const { taskVariant, setChannels } = useLavvaCreateActionContext();
  const { getChannelsByVariant } = useActionTaskVariants();
  const { inputValue, inputQuery, setInputValue } = useDebounce();
  const [selectedChannels, setSelectedChannels] = useState<ChannelType[]>([]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => setInputValue(event.target.value);

  const channelsByVariants = useMemo(() => {
    const channels = getChannelsByVariant(taskVariant).filter((x) =>
      x.alias.toLowerCase().includes(inputQuery.toLowerCase()),
    );

    return channelGroups
      .map((x) => ({ ...x, channels: x.channels.filter((a) => channels.some((b) => a.id === b.id)) }))
      .filter((x) => x.channels.length);
  }, [channelGroups, taskVariant, inputQuery]);

  const handleNext = () => {
    setChannels(selectedChannels);
    navigate(ROUTES.ActionChannelSettings());
  };

  const handleChannelOnChange = (id: string) => {
    const channel = channelGroups.flatMap((x) => x.channels).find((x) => x.id === id);

    if (channel) {
      const foundIndex = selectedChannels.findIndex((x) => x.id === channel.id);

      if (foundIndex === -1) {
        setSelectedChannels([...selectedChannels, channel]);
      } else {
        setSelectedChannels(selectedChannels.filter((_, index) => index !== foundIndex));
      }
    }
  };

  return (
    <ActionCreateDeviceVariants
      handleNext={handleNext}
      searchInput={inputValue}
      handleChange={handleChange}
      disabledSubmit={!selectedChannels.length}
    >
      {channelsByVariants.length ? (
        <ul>
          {channelsByVariants.map((item: ChannelGroupItem, typeIndex: number) => (
            <Accordion kind="custom" key={typeIndex} title={item.label} hideExpandedBorder expanded>
              <ul className="grid-list-24">
                {item.channels.map((ch: ChannelType) => {
                  const checked = !!selectedChannels.find((item) => item && item.id === ch.id);

                  return (
                    <li key={ch.id}>
                      <ChannelBox
                        channel={ch}
                        isListItem
                        isCheckbox
                        checked={checked}
                        onChannelToggle={handleChannelOnChange}
                        noRedirect
                      />
                    </li>
                  );
                })}
              </ul>
            </Accordion>
          ))}
        </ul>
      ) : (
        <EmptyStateBox content={t('noChannels')} />
      )}
    </ActionCreateDeviceVariants>
  );
};

export default LavvaActionCreateDeviceVariants;
